import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import Card from '@components/ui/Card'
import Button from '@components/ui/Button'
import Image from '@components/ui/Image'
import Loading from '@components/ui/Loading'
import { useAPI } from '@hooks/useAPI'
import { getFeaturedProducts } from '@services/api/products'
import { getFullAttachmentUrl } from '@services/api/index.js'

const FeaturedProducts = () => {
  const { t, i18n } = useTranslation()
  const { data: featuredProducts, loading, error } = useAPI(getFeaturedProducts, [i18n.language])

  if (loading) {
    return (
      <section className="py-10">
        <div className="container section-padding">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl font-bold text-gray-900">
              {t('home.featured.title')}
            </h2>
            <Link 
              to="/products"
              className="bg-blue-600 text-white hover:bg-blue-700 font-medium whitespace-nowrap px-4 py-2 rounded-md"
            >
              {t('home.featured.viewAll')}
            </Link>
          </div>
          <div className="flex justify-center">
            <Loading />
          </div>
        </div>
      </section>
    )
  }

  if (error) {
    return (
      <section className="py-10">
        <div className="container section-padding">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl font-bold text-gray-900">
              {t('home.featured.title')}
            </h2>
            <Link 
              to="/products"
              className="bg-blue-600 text-white hover:bg-blue-700 font-medium whitespace-nowrap px-4 py-2 rounded-md"
            >
              {t('home.featured.viewAll')}
            </Link>
          </div>
          <div className="text-center text-red-600">
            <p>{t('home.featured.loadError')}: {error}</p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section className="py-10">
      <div className="container section-padding">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl font-bold text-blue-600">
            {t('home.featured.title')}
          </h2>
          <Link 
            to="/products"
            className="bg-blue-600 text-white hover:bg-blue-700 font-medium whitespace-nowrap px-4 py-2 rounded-md"
          >
            {t('home.featured.viewAll')}
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {featuredProducts?.map((product) => (
            <Card key={product.productId} padding="none" className="h-full min-h-[380px] border border-gray-200 bg-gray-100 shadow-[0_8px_12px_-2px_rgba(0,0,0,0.15),0_4px_6px_-4px_rgba(0,0,0,0.1),8px_0_12px_-2px_rgba(0,0,0,0.15)] transform transition-all duration-300 hover:shadow-[0_16px_24px_-4px_rgba(0,0,0,0.2),0_8px_12px_-8px_rgba(0,0,0,0.15),12px_0_24px_-4px_rgba(0,0,0,0.2)] hover:-translate-y-1 flex flex-col">
              <div className="p-3 flex-grow flex flex-col">
                <div className="flex-1 h-[220px] bg-white rounded-md flex items-center justify-center overflow-hidden">
                  <div className="w-full h-full flex items-center justify-center">
                    <Image
                      src={getFullAttachmentUrl(product?.coverImage)}
                      alt={product.productName}
                      className="max-h-full max-w-full"
                      imgClassName="max-h-full max-w-full object-contain"
                    />
                  </div>
                </div>
                
                <div className="text-center mt-3 mb-2">
                  <p className="text-sm text-gray-900 font-medium line-clamp-2">
                    {product.productName}
                  </p>
                </div>
              </div>
              
              <div className="p-3 pt-0">
                <Button 
                  as={Link}
                  to={`/products/${product.productId}`}
                  variant="secondary" 
                  size="sm" 
                  className="bg-blue-600 text-white hover:bg-blue-700 px-8 py-2 rounded-md w-full"
                >
                  {t('common.viewDetails')}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  )
}

export default FeaturedProducts 